import { Link } from "react-router-dom";
import { ArrowLeft, Download, Shield, Cookie, FileText } from "lucide-react";
import { Reveal, RevealText } from "@/lib/motion";

const OWNER = {
  name: "Gefion S.r.l.",
  address: "Via 1° Maggio, 6 — 33070 Budoia (PN) — Italia",
  piva: "01675990939",
  pec: "gefionsrl@legalmail.it",
  email: "info@gefion-evolution.it",
  rep: "Enrico Manieri",
};

const Section = ({ n, title, children }) => (
  <section className="border-t border-[#1a1a1a] py-8 md:py-10">
    <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
      <div className="md:col-span-3">
        <div className="font-mono-tech text-[10px] text-white/40">§ {n}</div>
        <div className="font-display text-xl md:text-2xl mt-2 leading-tight">{title}</div>
      </div>
      <div className="md:col-span-9 text-white/75 text-[15px] leading-relaxed space-y-3">{children}</div>
    </div>
  </section>
);

export default function PrivacyPage() {
  return (
    <div className="pt-24 pb-24">
      <style>{`@media print{header,footer,.no-print{display:none!important}body{background:#fff!important;color:#000!important}.text-white,.text-white\\/70,.text-white\\/60,.text-white\\/75{color:#000!important}.border-\\[\\#1a1a1a\\]{border-color:#ddd!important}}`}</style>
      <section className="py-16 border-b border-[#141414]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <Reveal>
            <Link to="/" className="font-mono-tech text-white/60 hover:text-[#ff4b00] no-print"><ArrowLeft size={12} className="inline mr-1" />Home</Link>
          </Reveal>
          <Reveal delay={0.05}>
            <div className="num-chip my-6"><span className="dot" /> Documento legale · GDPR + D.Lgs. 196/2003</div>
          </Reveal>
          <RevealText text="Privacy Policy" className="font-display text-white text-[12vw] md:text-[6vw] leading-[0.9]" />
          <Reveal delay={0.2}>
            <p className="text-white/70 max-w-2xl mt-6">
              Informativa sul trattamento dei dati personali ai sensi dell'art. 13
              del Regolamento (UE) 2016/679 (GDPR) e del D.Lgs. 196/2003.
            </p>
            <div className="mt-8 flex gap-3 flex-wrap no-print">
              <button onClick={() => window.print()} data-testid="privacy-download-pdf" className="btn-primary"><Download size={14} /> Scarica / Stampa PDF</button>
              <Link to="/cookie-policy" className="btn-ghost"><Cookie size={14} /> Cookie Policy</Link>
              <Link to="/termini" className="btn-ghost"><FileText size={14} /> Termini di Servizio</Link>
            </div>
          </Reveal>
        </div>
      </section>

      <div className="max-w-[1200px] mx-auto px-6 md:px-10 pt-8">
        <Section n="01" title="Titolare del trattamento">
          <p>Il Titolare del trattamento dei dati è:</p>
          <div className="border border-[#1a1a1a] p-5 mt-3">
            <div><strong className="text-white">{OWNER.name}</strong></div>
            <div>{OWNER.address}</div>
            <div className="font-mono-tech text-[12px] mt-2">P.IVA {OWNER.piva}</div>
            <div className="font-mono-tech text-[12px]">PEC: {OWNER.pec}</div>
            <div className="font-mono-tech text-[12px]">Email: {OWNER.email}</div>
            <div className="mt-2 text-[13px]">Legale rappresentante: {OWNER.rep}</div>
          </div>
        </Section>

        <Section n="02" title="Dati che raccogliamo">
          <p>Quando interagisci con il nostro sito web possiamo raccogliere le seguenti categorie di dati personali:</p>
          <ul className="list-disc pl-6 space-y-1 mt-2">
            <li><strong className="text-white">Dati identificativi</strong>: nome, cognome, azienda, indirizzo</li>
            <li><strong className="text-white">Dati di contatto</strong>: email, numero di telefono, PEC</li>
            <li><strong className="text-white">Dati tecnici degli impianti</strong>: specifiche, indirizzo di installazione, foto (se caricate)</li>
            <li><strong className="text-white">File tecnici</strong>: disegni DWG/DXF caricati per il servizio di taglio laser</li>
            <li><strong className="text-white">Dati di navigazione</strong>: indirizzo IP, tipo di browser, pagine visitate — <em>raccolti solo previo consenso cookie</em></li>
          </ul>
        </Section>

        <Section n="03" title="Finalità del trattamento e base giuridica">
          <p>Trattiamo i tuoi dati personali esclusivamente per le seguenti finalità:</p>
          <ul className="list-disc pl-6 space-y-1 mt-2">
            <li>Rispondere alle richieste di informazioni ricevute tramite i moduli di contatto</li>
            <li>Elaborare preventivi per servizi di taglio laser, impianti e assistenza</li>
            <li>Eseguire ordini di taglio laser (produzione + spedizione)</li>
            <li>Gestire contratti di assistenza tecnica e ticket di supporto</li>
            <li>Adempiere agli obblighi fiscali, contabili e di legge</li>
          </ul>
          <p className="pt-3"><strong className="text-white">Base giuridica</strong>: esecuzione di misure precontrattuali/contrattuali (art. 6.1.b GDPR), adempimento di obblighi legali (art. 6.1.c), e — solo per dati non necessari all'esecuzione — consenso esplicito dell'interessato (art. 6.1.a).</p>
          <p className="text-[#ff4b00]"><strong>Non effettuiamo alcuna attività di profilazione o marketing automatizzato.</strong></p>
        </Section>

        <Section n="04" title="Periodo di conservazione">
          <ul className="list-disc pl-6 space-y-1">
            <li><strong className="text-white">Richieste di contatto senza contratto</strong>: 24 mesi dall'ultimo contatto</li>
            <li><strong className="text-white">Preventivi taglio laser + file DWG</strong>: 12 mesi dalla consegna dell'ordine, poi cancellazione automatica</li>
            <li><strong className="text-white">Contratti di assistenza attivi</strong>: durata del contratto + 10 anni (obblighi fiscali)</li>
            <li><strong className="text-white">Cookie tecnici</strong>: durata sessione. Cookie analitici: 12 mesi</li>
            <li><strong className="text-white">Log di consenso</strong>: 10 anni (prova legale)</li>
          </ul>
        </Section>

        <Section n="05" title="Comunicazione a soggetti terzi">
          <p>I tuoi dati possono essere condivisi esclusivamente con:</p>
          <ul className="list-disc pl-6 space-y-1 mt-2">
            <li><strong className="text-white">Stripe</strong> (payment processor USA) — dati di pagamento per esecuzione ordini. Coperto da Standard Contractual Clauses UE.</li>
            <li><strong className="text-white">Provider di hosting</strong> (infrastruttura cloud in UE) — Responsabile del trattamento nominato ai sensi art. 28 GDPR.</li>
            <li><strong className="text-white">Consulenti fiscali/legali</strong> — solo per adempimenti obbligatori.</li>
            <li><strong className="text-white">Autorità pubbliche</strong> — solo su richiesta legale documentata.</li>
          </ul>
          <p className="mt-3"><strong className="text-white">Non vendiamo, cediamo o comunichiamo i tuoi dati a terzi per finalità commerciali o di marketing.</strong></p>
        </Section>

        <Section n="06" title="Trasferimento dati extra-UE">
          <p>I tuoi dati sono archiviati e trattati all'interno dello Spazio Economico Europeo (SEE).</p>
          <p>L'unica eccezione riguarda il servizio Stripe (payment processor con sede USA), il cui trasferimento è coperto dalle Standard Contractual Clauses approvate dalla Commissione Europea (Decisione UE 2021/914) e dal Data Privacy Framework UE-USA.</p>
        </Section>

        <Section n="07" title="Diritti dell'interessato">
          <p>In qualsiasi momento puoi esercitare i seguenti diritti previsti dagli artt. 15-22 GDPR:</p>
          <ul className="list-disc pl-6 space-y-1 mt-2">
            <li>Accesso ai tuoi dati (art. 15)</li>
            <li>Rettifica (art. 16)</li>
            <li>Cancellazione — "diritto all'oblio" (art. 17)</li>
            <li>Limitazione del trattamento (art. 18)</li>
            <li>Portabilità dei dati (art. 20)</li>
            <li>Opposizione al trattamento (art. 21)</li>
            <li>Revoca del consenso in qualsiasi momento</li>
          </ul>
          <p className="mt-3">Per esercitare i tuoi diritti scrivi a: <a href={`mailto:${OWNER.email}`} className="text-[#ff4b00] hover:underline">{OWNER.email}</a> oppure via PEC a <a href={`mailto:${OWNER.pec}`} className="text-[#ff4b00] hover:underline">{OWNER.pec}</a>. Risponderemo entro 30 giorni.</p>
        </Section>

        <Section n="08" title="Reclamo al Garante Privacy">
          <p>Se ritieni che il trattamento dei tuoi dati violi il GDPR, puoi presentare reclamo al Garante per la protezione dei dati personali:</p>
          <p className="font-mono-tech text-[12px]">Piazza Venezia 11, 00187 Roma · <a href="https://www.garanteprivacy.it" target="_blank" rel="noreferrer" className="text-[#ff4b00] hover:underline">www.garanteprivacy.it</a></p>
        </Section>

        <Section n="09" title="Sicurezza dei dati">
          <p>Adottiamo misure tecniche e organizzative adeguate a proteggere i tuoi dati da accessi non autorizzati, perdita, distruzione o divulgazione: connessioni cifrate HTTPS, backup crittografati, controllo accessi con autenticazione a doppio fattore per il personale, hash bcrypt per le password.</p>
        </Section>

        <Section n="10" title="Modifiche all'informativa">
          <p>Ci riserviamo la facoltà di aggiornare la presente informativa. Le modifiche sostanziali saranno notificate via email agli utenti registrati e pubblicate su questa pagina con nuova data di aggiornamento.</p>
          <p className="font-mono-tech text-[11px] text-white/40 mt-4">Ultimo aggiornamento: {new Date().toLocaleDateString("it-IT", { year: "numeric", month: "long", day: "numeric" })}</p>
        </Section>
      </div>
    </div>
  );
}
